// The « Créer une partie » form (a table among friends, its rules), opened from the Lobby's « Entre amis »
// tile. The form's values live in the Lobby: the solo games use the same rules.
import React, { useState } from "react";
import { Bot, Info, Link2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { t } from "../../lib/i18n";

// One tip open at a time. Tapping another "i" while a tip is open switches
// straight to the new one: the open tip does not treat that tap as a click
// outside (which used to close it and swallow the tap), and a tip only clears
// the shared state if it is still the open one.
function InfoTip({ id, text, openTip, setOpenTip }) {
  return (
    <Popover open={openTip === id} onOpenChange={(o) => setOpenTip((cur) => (o ? id : cur === id ? null : cur))}>
      <PopoverTrigger asChild>
        <button type="button" data-info-tip="" className="inline-flex items-center text-slate-500 hover:text-amber-400 transition-colors shrink-0" onClick={(e) => e.stopPropagation()}>
          <Info className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        onInteractOutside={(e) => { if (e.target?.closest?.("[data-info-tip]")) e.preventDefault(); }}
        className="max-w-[220px] text-xs text-slate-300 bg-slate-900 border-slate-700 px-3 py-2 leading-relaxed"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

export function CreateRoomDialog({ open, onOpenChange, form, setForm, toggles, setToggles, onCreate }) {
  const [openTip, setOpenTip] = useState(null);
  const setOpen = (v) => { onOpenChange(v); if (!v) setOpenTip(null); };
  const handleCreate = onCreate;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md max-h-[92dvh] overflow-y-auto p-3 sm:p-5">
          <DialogHeader className="pb-1">
            <DialogTitle className="font-display text-lg sm:text-xl text-amber-300">
              {t("lobby.newGame")}
            </DialogTitle>
          </DialogHeader>
  
          <div className="space-y-2 sm:space-y-2.5">
            {/* Ligne 1 : Joueurs + Bots de départ */}
            <div className="grid gap-3 items-end grid-cols-2">
              <div>
                <Label className="text-slate-300 text-xs">{t("lobby.players")}</Label>
                <Select
                  value={String(form.num_players)}
                  onValueChange={(v) => {
                    const n = Number(v);
                    setForm({
                      ...form,
                      num_players: n,
                      num_bots: Math.min(form.num_bots, n - 1),
                    });
                  }}
                >
                  <SelectTrigger data-testid="num-players-select" className="bg-slate-800 border-white/10 text-white mt-1 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-white/10">
                    <SelectItem value="2">{t("lobby.nPlayers", { n: 2 })}</SelectItem>
                    <SelectItem value="3">{t("lobby.nPlayers", { n: 3 })}</SelectItem>
                    <SelectItem value="4">{t("lobby.nPlayers", { n: 4 })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs flex items-center gap-1">
                  <Bot className="h-3 w-3 text-emerald-400" />
                  {t("lobby.botsLabel", { n: form.num_bots, max: form.num_players - 1 })}
                </Label>
                <Select
                  value={String(form.num_bots)}
                  onValueChange={(v) => setForm({ ...form, num_bots: Number(v) })}
                >
                  <SelectTrigger data-testid="num-bots-select" className="bg-slate-800 border-white/10 text-white mt-1 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-white/10">
                    {Array.from({ length: form.num_players }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {t("lobby.nBots", { n: i })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
  
            {/* Niveau du bot — affiché si au moins 1 bot */}
            {form.num_bots > 0 && (
              <div>
                <Label className="text-slate-300 text-xs flex items-center gap-1">
                  <Bot className="h-3 w-3 text-emerald-400" />
                  {t("lobby.botLevel")}
                </Label>
                <Select
                  value={form.bot_difficulty}
                  onValueChange={(v) => setForm({ ...form, bot_difficulty: v })}
                >
                  <SelectTrigger data-testid="bot-difficulty-select" className="bg-slate-800 border-white/10 text-white mt-1 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-white/10">
                    <SelectItem value="debutant">{t("lobby.level.debutant")}</SelectItem>
                    <SelectItem value="medium">{t("lobby.level.medium")}</SelectItem>
                    <SelectItem value="legende">{t("lobby.level.legende")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
  
            {/* Ligne 2 : Jokers + Seuil initial + Score élimination */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-300 text-xs flex items-center">{t("common.jokers")}</Label>
                <Select
                  value={String(form.num_jokers)}
                  onValueChange={(v) => setForm({ ...form, num_jokers: Number(v) })}
                >
                  <SelectTrigger data-testid="num-jokers-select" className="bg-slate-800 border-white/10 text-white mt-1 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-white/10">
                    <SelectItem value="4">{t("lobby.nJokers", { n: 4 })}</SelectItem>
                    <SelectItem value="6">{t("lobby.nJokers", { n: 6 })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs flex items-center gap-1">
                  <InfoTip id="threshold" text={t("lobby.thresholdTip")} openTip={openTip} setOpenTip={setOpenTip} />
                  {t("lobby.thresholdLabel")}
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  data-testid="threshold-x-input"
                  value={form.threshold_x || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").replace(/^0+(\d)/, "$1");
                    setForm({ ...form, threshold_x: v === "" ? 0 : parseInt(v, 10) });
                  }}
                  className="bg-slate-800 border-white/10 text-white mt-1 h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs flex items-center gap-1">
                  <InfoTip id="elim" text={t("lobby.elimTip")} openTip={openTip} setOpenTip={setOpenTip} />
                  {t("lobby.elimLabel")}
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  data-testid="elim-y-input"
                  value={form.elim_score_y || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").replace(/^0+(\d)/, "$1");
                    setForm({ ...form, elim_score_y: v === "" ? 0 : parseInt(v, 10) });
                  }}
                  className="bg-slate-800 border-white/10 text-white mt-1 h-8 text-sm"
                />
              </div>
            </div>
  
            {/* Pénalités : chaque ligne = titre | toggle | valeur. En tunisien (RTL)
                le toggle passe en dernier, donc tout à gauche, à gauche de la valeur. */}
            <div className="rami-glass rounded-xl p-2 space-y-1.5">
              <div className="text-amber-300 text-[10px] font-semibold uppercase tracking-wide mb-0.5">
                {t("lobby.rulesTitle")}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <InfoTip id="pure-seq" text={t("lobby.pureSeqTip")} openTip={openTip} setOpenTip={setOpenTip} />
                <span className="text-slate-200 text-xs flex-1 min-w-0 leading-tight select-none">
                  {t("lobby.pureSeq")}
                </span>
                <Switch
                  id="toggle-pure-seq"
                  data-testid="toggle-pure-sequence"
                  className="rtl:order-last"
                  checked={toggles.pureSequence}
                  onCheckedChange={(v) => setToggles({ ...toggles, pureSequence: v })}
                />
                <div className="w-14 shrink-0" />
              </div>
              {[
                { id: "toggle-standard",      testId: "toggle-standard-penalty",       label: t("lobby.pen.standard.label"),      toggleKey: "standard",      field: "standard_penalty",        inputTestId: "standard-penalty-input",  tip: t("lobby.pen.standard.tip") },
                { id: "toggle-joker",         testId: "toggle-joker-penalty",          label: t("lobby.pen.joker.label"),         toggleKey: "joker",         field: "joker_in_hand_penalty",   inputTestId: "joker-hand-penalty-input", tip: t("lobby.pen.joker.tip") },
                { id: "toggle-useful",        testId: "toggle-useful-discard",         label: t("lobby.pen.useful.label"),        toggleKey: "usefulDiscard", field: "useful_discard_penalty",  inputTestId: "useful-discard-penalty-input", tip: t("lobby.pen.useful.tip") },
                { id: "toggle-continuer",     testId: "toggle-continuer-penalty",      label: t("lobby.pen.continuer.label"),     toggleKey: "continuer",     field: "continuer_penalty",       inputTestId: "continuer-penalty-input", tip: t("lobby.pen.continuer.tip") },
                { id: "toggle-nefrech-fausse",testId: "toggle-nefrech-fausse-penalty", label: t("lobby.pen.nefrechFausse.label"), toggleKey: "nefrechFausse", field: "nefrech_fausse_penalty",  inputTestId: "nefrech-fausse-penalty-input", tip: t("lobby.pen.nefrechFausse.tip") },
              ].map(({ id, testId, label, toggleKey, field, inputTestId, tip }) => (
                <div key={id} className="flex items-center gap-2 min-w-0">
                  <InfoTip id={id} text={tip} openTip={openTip} setOpenTip={setOpenTip} />
                  <span className="text-slate-200 text-xs flex-1 min-w-0 leading-tight select-none">
                    {label}
                  </span>
                  <Switch
                    id={id}
                    data-testid={testId}
                    className="rtl:order-last"
                    checked={toggles[toggleKey]}
                    onCheckedChange={(v) => setToggles({ ...toggles, [toggleKey]: v })}
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    data-testid={inputTestId}
                    value={form[field] || ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").replace(/^0+(\d)/, "$1");
                      setForm({ ...form, [field]: v === "" ? 0 : parseInt(v, 10) });
                    }}
                    disabled={!toggles[toggleKey]}
                    className="w-14 h-7 text-xs bg-slate-800 border-white/10 text-white disabled:opacity-40 shrink-0 px-1.5"
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 pt-3">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t("lobby.createCancel")}
            </Button>
            <Button
              data-testid="confirm-create-room-btn"
              onClick={handleCreate}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            >
              <Link2 className="h-3.5 w-3.5 me-1.5" />{t("lobby.createInvite")}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
}

export default CreateRoomDialog;
